import { Link } from 'react-router-dom';
import { useStore, Panel, Tile, Badge, Meter, Empty, plural } from '../components/ui.jsx';
import {
  dashboardStats, scheduleBetween, today, attentionQueue, weekAhead,
  staffById, ATTENTION,
} from '../data/store.js';
import { format } from '../lib/money.js';

function timeLabel(occ) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: occ.timeZone, hour: 'numeric', minute: '2-digit',
  }).format(new Date(occ.startsAt));
}

const ATTENTION_LABEL = {
  [ATTENTION.PAST_DUE]: 'Past due',
  [ATTENTION.NO_WAIVER]: 'No waiver',
  [ATTENTION.CREDITS_LOW]: 'Low credits',
  [ATTENTION.CREDITS_EXPIRING]: 'Expiring',
  [ATTENTION.MEMBERSHIP_ENDING]: 'Ending',
  [ATTENTION.HOLD_ENDING]: 'Hold ending',
};

const ATTENTION_TONE = {
  [ATTENTION.PAST_DUE]: 'bad',
  [ATTENTION.NO_WAIVER]: 'bad',
  [ATTENTION.CREDITS_LOW]: 'neutral',
  [ATTENTION.CREDITS_EXPIRING]: 'warn',
  [ATTENTION.MEMBERSHIP_ENDING]: 'warn',
  [ATTENTION.HOLD_ENDING]: 'neutral',
};

/**
 * Seven days of booked seats.
 *
 * Bars rather than a chart library: the question is "which evenings are full
 * and which are empty", and the answer is a shape, not a set of numbers.
 */
function WeekStrip() {
  const days = weekAhead(7);
  const totalBooked = days.reduce((a, d) => a + d.booked, 0);
  const totalSeats = days.reduce((a, d) => a + d.capacity, 0);

  return (
    <Panel
      title="The week ahead"
      actions={(
        <span className="sub num">
          {totalSeats ? `${Math.round((totalBooked / totalSeats) * 100)}% of ${totalSeats} seats` : 'No classes'}
        </span>
      )}
    >
      <div className="weekstrip">
        {days.map((d, i) => {
          // Height is how FULL the day is, not how it compares to the busiest
          // day. Scaling to the peak makes the best day always look sold out,
          // which is exactly the wrong thing to tell an owner.
          const fill = d.capacity ? (d.booked / d.capacity) * 100 : 0;
          const label = new Intl.DateTimeFormat('en-US', {
            weekday: 'short', timeZone: 'UTC',
          }).format(new Date(`${d.date}T12:00:00Z`));
          return (
            <div className="weekstrip-day" key={d.date}>
              <div
                className={d.classes === 0 ? 'weekstrip-bar is-empty' : 'weekstrip-bar'}
                title={d.classes === 0
                  ? `${d.date} — no classes`
                  : `${d.date} — ${d.booked} of ${d.capacity} seats across ${plural(d.classes, 'class')}`}
              >
                {/* A booked seat always leaves a visible mark, however small;
                    a day with classes and no bookings shows an empty track. */}
                <i style={{ height: `${d.booked > 0 ? Math.max(fill, 5) : 0}%` }} />
              </div>
              <span className="num">{d.classes === 0 ? '—' : d.booked}</span>
              <span className={i === 0 ? 'weekstrip-label is-today' : 'weekstrip-label'}>
                {i === 0 ? 'Today' : label}
              </span>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

export default function Dashboard() {
  const state = useStore();
  const stats = dashboardStats();
  const t = today();
  const classes = scheduleBetween(t, t);
  const attention = attentionQueue();

  return (
    <>
      <header className="topbar">
        <h1>Dashboard</h1>
        <span className="sub">
          {/* The gym's date, not the browser's. today() is already resolved in
              the org's zone, so a bare new Date() here printed tomorrow above
              a schedule built for today whenever the two disagreed. */}
          {new Intl.DateTimeFormat('en-US', {
            dateStyle: 'full', timeZone: state.org.timeZone || 'UTC',
          }).format(new Date())}
        </span>
      </header>

      <div className="page">
        <div className="tiles">
          <Tile label="Active members" value={stats.activeMembers} foot={`${stats.frozenCount} on hold`} />
          <Tile label="MRR" value={format(stats.mrrCents)} foot="Normalised across intervals" />
          <Tile
            label="Checked in today"
            value={stats.checkedInToday}
            foot={stats.expectedToday
              ? `of ${plural(stats.expectedToday, 'booked seat', 'booked seats')}`
              : 'Nobody booked yet'}
          />
          <Tile
            label="Past due"
            value={stats.pastDueCount}
            foot={stats.pastDueCents ? `${format(stats.pastDueCents)} outstanding` : 'All collected'}
          />
          <Tile
            label="Classes today"
            value={stats.classesToday}
            foot={`${stats.seatsToday.filled}/${stats.seatsToday.capacity} seats · ${stats.fillRate}% full`}
          />
        </div>

        <div className="split">
          <div className="stack">
            <Panel title="Today's schedule" bodyless>
              {classes.length === 0 ? (
                <Empty>No classes scheduled today.</Empty>
              ) : (
                <table className="data">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Class</th>
                      <th>Coach</th>
                      <th>Seats</th>
                      <th className="right">Waitlist</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map((occ) => (
                      <tr key={occ.id}>
                        <td className="mono num">{timeLabel(occ)}</td>
                        <td><Link to={`/schedule/${encodeURIComponent(occ.id)}`}>{occ.name}</Link></td>
                        <td>{staffById(occ.staffId)?.name ?? '—'}</td>
                        <td>
                          <div className="row">
                            <span className="mono num">{occ.booked}/{occ.capacity}</span>
                            <Meter value={occ.booked} max={occ.capacity} />
                          </div>
                        </td>
                        <td className="right">
                          {occ.waitlisted > 0 ? <Badge tone="warn">{`${occ.waitlisted} waiting`}</Badge> : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            <WeekStrip />
          </div>

          <Panel
            title="Needs attention"
            actions={attention.length > 0 && (
              <span className="sub num">{attention.filter((a) => a.urgent).length} urgent</span>
            )}
            bodyless
          >
            {attention.length === 0 ? (
              <Empty>Nothing needs you right now. Good week.</Empty>
            ) : (
              <table className="data">
                <thead>
                  <tr><th>Member</th><th>What</th></tr>
                </thead>
                <tbody>
                  {attention.map((item, i) => (
                    <tr key={`${item.kind}-${item.memberId}-${i}`}>
                      <td>
                        {item.member
                          ? <Link to={`/members/${item.member.id}`}>{item.member.name}</Link>
                          : <span className="mono">{item.memberId}</span>}
                        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{item.detail}</div>
                      </td>
                      <td className="right">
                        <Badge tone={ATTENTION_TONE[item.kind]}>{ATTENTION_LABEL[item.kind]}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}
